import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { DatePicker } from "@/components/ui/date-picker";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TablePagination } from "@/shared/components/TablePagination";
import { ReportLookup } from "../components/ReportLookup";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportTable } from "../components/ReportTable";
import { useGreenhouses } from "@/features/farms/hooks/useGreenhouses";
import { useInputTypes } from "@/features/inventory/hooks/useInputTypes";
import { InputLookup } from "@/features/inventory/components/inputs/InputLookup";
import { useSuppliers } from "@/features/inventory/hooks/useSuppliers";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { useAllCropTypes } from "@/features/production/hooks/useCropTypes";
import { useDepartments } from "@/shared/hooks/useDepartments";
import { useReportQuery } from "../hooks/useReports";
import { reportsApi } from "../services/reportsApi";
import type {
  CostComparisonReportResponse,
  InputCostsReportResponse,
  MaterialUsageReportResponse,
  OrderProfitabilityReportResponse,
  PaymentSummaryReportResponse,
  RevenueAccountsReceivableSummaryReportResponse,
  RevenueByCustomerReportResponse,
  RevenueByProductReportResponse,
  RevenueByPeriodReportResponse,
  RevenueSummaryReportResponse,
  ProductionCostsReportResponse,
  ProfitabilitySummaryReportResponse,
  ReportType,
  ReportsQueryFilters,
} from "../services/reportsApi";

const REPORT_OPTIONS: { value: ReportType; label: string }[] = [
  { value: "production-costs", label: "Production Costs" },
  { value: "input-costs", label: "Input Costs" },
  { value: "material-usage", label: "Material Usage" },
  { value: "profitability-summary", label: "Profitability Summary" },
  { value: "cost-comparison", label: "Cost Comparison" },
  { value: "order-profitability", label: "Order Profitability" },
  { value: "payment-summary", label: "Payment Summary" },
  { value: "revenue-summary", label: "Revenue Reports" },
];

const REVENUE_REPORT_OPTIONS: { value: ReportType; label: string }[] = [
  { value: "revenue-summary", label: "Revenue Summary" },
  { value: "revenue-by-period", label: "Revenue By Period" },
  { value: "revenue-by-customer", label: "Revenue By Customer" },
  { value: "revenue-by-product", label: "Revenue By Product" },
  { value: "revenue-ar-summary", label: "Accounts Receivable Summary" },
];

const isRevenueReport = (reportType: ReportType) =>
  reportType === "revenue-summary" ||
  reportType === "revenue-by-period" ||
  reportType === "revenue-by-customer" ||
  reportType === "revenue-by-product" ||
  reportType === "revenue-ar-summary";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-KE").format(value ?? 0);

const formatPercent = (value: number) => `${(value ?? 0).toFixed(2)}%`;

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-KE", { timeZone: "Africa/Nairobi" });
};

const MetricCard = ({ label, value }: { label: string; value: string }) => (
  <Card className="gap-2 py-4">
    <CardHeader className="pb-0">
      <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
    </CardContent>
  </Card>
);

export const ReportsPage = () => {
  const [selectedReport, setSelectedReport] = useState<ReportType>("production-costs");
  const [filters, setFilters] = useState<ReportsQueryFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<ReportsQueryFilters>({});
  const [runToken, setRunToken] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [materialPaging, setMaterialPaging] = useState({ pageNumber: 1, pageSize: 50, groupPageNumber: 1, groupPageSize: 50 });

  const isMaterialUsage = selectedReport === "material-usage";
  const { data: greenhouses = [] } = useGreenhouses(selectedReport === "production-costs" || isMaterialUsage);
  const { data: inputTypes = [] } = useInputTypes(selectedReport === "input-costs" || isMaterialUsage);
  const { data: suppliers = [] } = useSuppliers(selectedReport === "input-costs");
  const { data: cropTypes = [] } = useAllCropTypes(selectedReport === "profitability-summary" || isMaterialUsage);
  const { data: departments = [] } = useDepartments(isMaterialUsage);

  const reportQuery = useReportQuery(selectedReport, isMaterialUsage ? {
    ...submittedFilters,
    pageNumber: String(materialPaging.pageNumber), pageSize: String(materialPaging.pageSize),
    groupPageNumber: String(materialPaging.groupPageNumber), groupPageSize: String(materialPaging.groupPageSize),
  } : submittedFilters, runToken);
  const reportData = runToken > 0 ? reportQuery.data : undefined;
  const hasUnappliedFilters = Object.keys({ ...filters, ...submittedFilters }).some(
    key => (filters[key as keyof ReportsQueryFilters] || "") !== (submittedFilters[key as keyof ReportsQueryFilters] || "")
  );

  const updateFilter = (key: keyof ReportsQueryFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const clearReportSpecificFilters = (reportType: ReportType) => {
    switch (reportType) {
      case "production-costs":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          greenhouseId: filters.greenhouseId,
          sowingInstructionId: filters.sowingInstructionId,
        });
        return;
      case "input-costs":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          inputTypeId: filters.inputTypeId,
          supplierId: filters.supplierId,
        });
        return;
      case "material-usage":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          departmentId: filters.departmentId,
          greenhouseId: filters.greenhouseId,
          fieldId: filters.fieldId,
          inputTypeId: filters.inputTypeId,
          inputId: filters.inputId,
          cropTypeId: filters.cropTypeId,
          variety: filters.variety,
          sowingInstructionId: filters.sowingInstructionId,
          requisitionId: filters.requisitionId,
          sourceReferenceType: filters.sourceReferenceType,
          sourceReferenceNumber: filters.sourceReferenceNumber,
          groupBy: filters.groupBy,
        });
        return;
      case "profitability-summary":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          customerId: filters.customerId,
          cropTypeId: filters.cropTypeId,
        });
        return;
      case "cost-comparison":
        setFilters({
          period1Start: filters.period1Start,
          period1End: filters.period1End,
          period2Start: filters.period2Start,
          period2End: filters.period2End,
        });
        return;
      case "order-profitability":
        setFilters({
          orderId: filters.orderId,
        });
        return;
      case "payment-summary":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          orderId: filters.orderId,
        });
        return;
      case "revenue-summary":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          orderId: filters.orderId,
        });
        return;
      case "revenue-by-period":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
          granularity: filters.granularity,
        });
        return;
      case "revenue-by-customer":
      case "revenue-by-product":
        setFilters({
          startDate: filters.startDate,
          endDate: filters.endDate,
        });
        return;
      case "revenue-ar-summary":
        setFilters({
          asOf: filters.asOf,
        });
        return;
    }
  };

  const handleReportTypeChange = (value: string) => {
    const reportType = value as ReportType;
    setSelectedReport(reportType);
    setRunToken(0);
    setSubmittedFilters({});
    clearReportSpecificFilters(reportType);
    setValidationError(null);
  };

  const handleRevenueReportTypeChange = (value: string) => {
    const reportType = value as ReportType;
    setSelectedReport(reportType);
    setRunToken(0);
    setSubmittedFilters({});
    clearReportSpecificFilters(reportType);
    setValidationError(null);
  };

  const handleRunReport = () => {
    if (selectedReport === "cost-comparison") {
      if (!filters.period1Start || !filters.period1End || !filters.period2Start || !filters.period2End) {
        setValidationError("All period dates are required for cost comparison.");
        return;
      }

      if (filters.period1Start > filters.period1End) {
        setValidationError("Period 1 start date cannot be after period 1 end date.");
        return;
      }

      if (filters.period2Start > filters.period2End) {
        setValidationError("Period 2 start date cannot be after period 2 end date.");
        return;
      }
    } else if (selectedReport === "order-profitability") {
      if (!filters.orderId) {
        setValidationError("Please select an order for order profitability.");
        return;
      }
    } else if (isRevenueReport(selectedReport) && selectedReport !== "revenue-ar-summary") {
      if (!filters.startDate || !filters.endDate) {
        setValidationError("Start date and end date are required for revenue reports.");
        return;
      }

      if (filters.startDate > filters.endDate) {
        setValidationError("Start date cannot be after end date.");
        return;
      }
    } else if (selectedReport === "revenue-ar-summary" && !filters.asOf) {
      setValidationError("As of date is required for accounts receivable summary.");
      return;
    } else if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setValidationError("Start date cannot be after end date.");
      return;
    }

    setValidationError(null);
    if (isMaterialUsage && (materialPaging.pageNumber !== 1 || materialPaging.groupPageNumber !== 1)) {
      setMaterialPaging(previous => ({ ...previous, pageNumber: 1, groupPageNumber: 1 }));
      setSubmittedFilters({ ...filters });
      setRunToken(previous => previous + 1);
    } else if (runToken > 0 && !hasUnappliedFilters) {
      void reportQuery.refetch();
    } else {
      setSubmittedFilters({ ...filters });
      setRunToken((prev) => prev + 1);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setSubmittedFilters({});
    setMaterialPaging({ pageNumber: 1, pageSize: 50, groupPageNumber: 1, groupPageSize: 50 });
    setRunToken(0);
    setValidationError(null);
  };

  const canExportProductionCosts = selectedReport === "production-costs" && !!reportData && !validationError;
  const canExportInputCosts = selectedReport === "input-costs" && !!reportData && !validationError;

  const canExportMaterialUsage =
    false;

  const canExportCostComparisonExcel =
    selectedReport === "cost-comparison" &&
    !!submittedFilters.period1Start &&
    !!submittedFilters.period1End &&
    !!submittedFilters.period2Start &&
    !!submittedFilters.period2End &&
    !!reportData &&
    !validationError;

  const canExportProfitabilitySummaryPdf =
    selectedReport === "profitability-summary" &&
    !!reportData &&
    !validationError;

  const canExportProfitabilitySummaryExcel = canExportProfitabilitySummaryPdf;

  const canExportOrderProfitabilityPdf =
    selectedReport === "order-profitability" &&
    !!submittedFilters.orderId &&
    !!reportData &&
    !validationError;

  const canExportOrderProfitabilityExcel = canExportOrderProfitabilityPdf;

  const canExportRevenueSummaryExcel =
    selectedReport === "revenue-summary" && !!reportData && !validationError;
  const canExportRevenueSummaryPdf = canExportRevenueSummaryExcel;

  const canExportRevenueByPeriodExcel =
    selectedReport === "revenue-by-period" && !!reportData && !validationError;
  const canExportRevenueByPeriodPdf = canExportRevenueByPeriodExcel;

  const canExportRevenueByCustomerExcel =
    selectedReport === "revenue-by-customer" && !!reportData && !validationError;
  const canExportRevenueByCustomerPdf = canExportRevenueByCustomerExcel;

  const canExportRevenueByProductExcel =
    selectedReport === "revenue-by-product" && !!reportData && !validationError;
  const canExportRevenueByProductPdf = canExportRevenueByProductExcel;

  const canExportRevenueArSummaryExcel =
    selectedReport === "revenue-ar-summary" && !!reportData && !validationError;
  const canExportRevenueArSummaryPdf = canExportRevenueArSummaryExcel;

  const canExportExcel =
    canExportProductionCosts ||
    canExportInputCosts ||
    canExportMaterialUsage ||
    canExportCostComparisonExcel ||
    canExportProfitabilitySummaryExcel ||
    canExportOrderProfitabilityExcel ||
    canExportRevenueSummaryExcel ||
    canExportRevenueByPeriodExcel ||
    canExportRevenueByCustomerExcel ||
    canExportRevenueByProductExcel ||
    canExportRevenueArSummaryExcel;

  const canExportPdf =
    canExportProductionCosts ||
    canExportInputCosts ||
    canExportMaterialUsage ||
    canExportCostComparisonExcel ||
    canExportProfitabilitySummaryPdf ||
    canExportOrderProfitabilityPdf ||
    canExportRevenueSummaryPdf ||
    canExportRevenueByPeriodPdf ||
    canExportRevenueByCustomerPdf ||
    canExportRevenueByProductPdf ||
    canExportRevenueArSummaryPdf;

  const triggerFileDownload = (blob: Blob, fileName: string) => {
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleExportExcel = async () => {
    if (!canExportExcel) {
      return;
    }

    try {
      setIsExportingExcel(true);
      let exportFile;

      switch (selectedReport) {
        case "production-costs":
          exportFile = await reportsApi.exportProductionCostsReport(submittedFilters);
          break;
        case "input-costs":
          exportFile = await reportsApi.exportInputCostsReport(submittedFilters);
          break;
        case "material-usage":
          return;
        case "cost-comparison":
          exportFile = await reportsApi.exportCostComparisonReport(submittedFilters);
          break;
        case "profitability-summary":
          exportFile = await reportsApi.exportProfitabilitySummaryReport(submittedFilters);
          break;
        case "order-profitability":
          if (!submittedFilters.orderId) {
            return;
          }
          exportFile = await reportsApi.exportOrderProfitabilityReport(submittedFilters.orderId);
          break;
        case "revenue-summary":
          exportFile = await reportsApi.exportRevenueSummaryReport(submittedFilters);
          break;
        case "revenue-by-period":
          exportFile = await reportsApi.exportRevenueByPeriodReport(submittedFilters);
          break;
        case "revenue-by-customer":
          exportFile = await reportsApi.exportRevenueByCustomerReport(submittedFilters);
          break;
        case "revenue-by-product":
          exportFile = await reportsApi.exportRevenueByProductReport(submittedFilters);
          break;
        case "revenue-ar-summary":
          exportFile = await reportsApi.exportRevenueAccountsReceivableSummaryReport(
            submittedFilters
          );
          break;
        default:
          return;
      }

      const { blob, fileName } = exportFile;
      triggerFileDownload(blob, fileName);
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    if (!canExportPdf) {
      return;
    }

    try {
      setIsExportingPdf(true);
      let exportFile;

      switch (selectedReport) {
        case "production-costs":
          exportFile = await reportsApi.exportProductionCostsReportPdf(submittedFilters);
          break;
        case "input-costs":
          exportFile = await reportsApi.exportInputCostsReportPdf(submittedFilters);
          break;
        case "material-usage":
          return;
        case "profitability-summary":
          exportFile = await reportsApi.exportProfitabilitySummaryReportPdf(submittedFilters);
          break;
        case "cost-comparison":
          exportFile = await reportsApi.exportCostComparisonReportPdf(submittedFilters);
          break;
        case "order-profitability":
          if (!submittedFilters.orderId) {
            return;
          }
          exportFile = await reportsApi.exportOrderProfitabilityReportPdf(submittedFilters.orderId);
          break;
        case "revenue-summary":
          exportFile = await reportsApi.exportRevenueSummaryReportPdf(submittedFilters);
          break;
        case "revenue-by-period":
          exportFile = await reportsApi.exportRevenueByPeriodReportPdf(submittedFilters);
          break;
        case "revenue-by-customer":
          exportFile = await reportsApi.exportRevenueByCustomerReportPdf(submittedFilters);
          break;
        case "revenue-by-product":
          exportFile = await reportsApi.exportRevenueByProductReportPdf(submittedFilters);
          break;
        case "revenue-ar-summary":
          exportFile = await reportsApi.exportRevenueAccountsReceivableSummaryReportPdf(
            submittedFilters
          );
          break;
        default:
          return;
      }

      const { blob, fileName } = exportFile;
      triggerFileDownload(blob, fileName);
    } finally {
      setIsExportingPdf(false);
    }
  };

  const renderProductionCosts = (data: ProductionCostsReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Labor Cost" value={formatCurrency(data.totalLaborCost)} />
        <MetricCard label="Total Material Cost" value={formatCurrency(data.totalMaterialCost)} />
        <MetricCard label="Total Activity Cost" value={formatCurrency(data.totalActivityCost)} />
        <MetricCard label="Activity Count" value={formatNumber(data.activityCount)} />
      </div>

      <ReportTable
        title="Costs By Activity Type"
        columns={[
          "Activity Type",
          "Labor Cost",
          "Material Cost",
          "Total Cost",
          "Activity Count",
        ]}
        rows={data.costsByActivityType.map((item) => [
          item.activityTypeName,
          formatCurrency(item.laborCost),
          formatCurrency(item.materialCost),
          formatCurrency(item.totalCost),
          formatNumber(item.activityCount),
        ])}
      />

      <ReportTable
        title="Costs By Greenhouse"
        columns={[
          "Greenhouse",
          "Labor Cost",
          "Material Cost",
          "Total Cost",
          "Activity Count",
        ]}
        rows={data.costsByGreenhouse.map((item) => [
          item.greenhouseName,
          formatCurrency(item.totalLaborCost),
          formatCurrency(item.totalMaterialCost),
          formatCurrency(item.totalCost),
          formatNumber(item.activityCount),
        ])}
      />

      <ReportTable
        title="Top Cost Activities"
        columns={[
          "Date",
          "Activity",
          "Greenhouse",
          "Performed By",
          "Labor Cost",
          "Material Cost",
          "Total Cost",
        ]}
        rows={data.topCostActivities.map((item) => [
          formatDate(item.activityDate),
          item.activityTypeName,
          item.greenhouseName,
          item.performedBy,
          formatCurrency(item.laborCost),
          formatCurrency(item.materialCost),
          formatCurrency(item.totalCost),
        ])}
      />
    </div>
  );

  const renderInputCosts = (data: InputCostsReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Purchase Cost" value={formatCurrency(data.totalPurchaseCost)} />
        <MetricCard label="Total Usage Cost" value={formatCurrency(data.totalUsageCost)} />
        <MetricCard label="Purchase Count" value={formatNumber(data.purchaseCount)} />
        <MetricCard label="Usage Count" value={formatNumber(data.usageCount)} />
      </div>

      <ReportTable
        title="Costs By Input Type"
        columns={[
          "Input Type",
          "Purchase Cost",
          "Usage Cost",
          "Avg Unit Cost",
          "Purchase Count",
        ]}
        rows={data.costsByInputType.map((item) => [
          item.inputTypeName,
          formatCurrency(item.totalPurchaseCost),
          formatCurrency(item.totalUsageCost),
          formatCurrency(item.averageUnitCost),
          formatNumber(item.purchaseCount),
        ])}
      />

      <ReportTable
        title="Costs By Supplier"
        columns={["Supplier", "Purchase Cost", "Avg Unit Cost", "Purchase Count"]}
        rows={data.costsBySupplier.map((item) => [
          item.supplierName,
          formatCurrency(item.totalPurchaseCost),
          formatCurrency(item.averageUnitCost),
          formatNumber(item.purchaseCount),
        ])}
      />

      <ReportTable
        title="Top Purchases"
        columns={[
          "Date",
          "Input",
          "Supplier",
          "Quantity",
          "Unit Cost",
          "Total Cost",
          "Batch",
        ]}
        rows={data.topPurchases.map((item) => [
          formatDate(item.movementDate),
          item.inputName,
          item.supplierName,
          `${formatNumber(item.quantity)} ${item.unit}`,
          formatCurrency(item.unitCost),
          formatCurrency(item.totalCost),
          item.batchNumber,
        ])}
      />
    </div>
  );

  const renderMaterialUsage = (data: MaterialUsageReportResponse) => (
    <div className="space-y-4" aria-busy={reportQuery.isFetching}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Issued Quantity" value={formatNumber(data.totalIssuedQuantity)} />
        <MetricCard label="Returned Quantity" value={formatNumber(data.totalReturnedQuantity)} />
        <MetricCard label="Net Used Quantity" value={formatNumber(data.netUsedQuantity)} />
        <MetricCard label="Net Usage Cost" value={formatCurrency(data.netUsageCost)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Issue Movements" value={formatNumber(data.issueCount)} />
        <MetricCard label="Return Movements" value={formatNumber(data.returnCount)} />
        <MetricCard label="Movement Rows" value={formatNumber(data.totalCount)} />
        <MetricCard label="Groups" value={formatNumber(data.totalGroupCount)} />
      </div>

      <p className="text-sm text-muted-foreground">Totals cover all matching movements. Tables are paged separately.</p>
      <ReportTable
        title="Grouped Usage"
        numericColumns={[1, 2, 3, 4, 5]}
        footer={<TablePagination ariaLabel="Usage groups pagination" pageNumber={data.groupPageNumber} pageSize={data.groupPageSize}
          totalCount={data.totalGroupCount} totalPages={Math.ceil(data.totalGroupCount / data.groupPageSize)}
          onPageChange={groupPageNumber => setMaterialPaging(previous => ({ ...previous, groupPageNumber }))}
          onPageSizeChange={groupPageSize => setMaterialPaging(previous => ({ ...previous, groupPageSize, groupPageNumber: 1 }))}
          isFetching={reportQuery.isFetching} />}
        columns={[
          "Group",
          "Issued Qty",
          "Returned Qty",
          "Net Qty",
          "Net Cost",
          "Movements",
        ]}
        rows={data.groups.map((group) => [
          group.groupLabel,
          formatNumber(group.totalIssuedQuantity),
          formatNumber(group.totalReturnedQuantity),
          formatNumber(group.netUsedQuantity),
          formatCurrency(group.netUsageCost),
          formatNumber(group.movementCount),
        ])}
      />

      <ReportTable
        title="Movement Details"
        numericColumns={[10, 11, 12, 13]}
        footer={<TablePagination ariaLabel="Movement details pagination" pageNumber={data.pageNumber} pageSize={data.pageSize}
          totalCount={data.totalCount} totalPages={Math.ceil(data.totalCount / data.pageSize)}
          onPageChange={pageNumber => setMaterialPaging(previous => ({ ...previous, pageNumber }))}
          onPageSizeChange={pageSize => setMaterialPaging(previous => ({ ...previous, pageSize, pageNumber: 1 }))}
          isFetching={reportQuery.isFetching} />}
        columns={[
          "Date",
          "Type",
          "Department",
          "Activity",
          "Purpose",
          "Recorded By",
          "Recorded At",
          "Source",
          "Input",
          "Input Type",
          "Qty",
          "Signed Qty",
          "Unit Cost",
          "Signed Cost",
          "Requisition",
          "Variety",
          "Reference",
        ]}
        rows={data.rows.map((row) => [
          formatDate(row.movementDate),
          row.movementType,
          row.departmentName,
          row.activityType ?? "-",
          row.purpose ?? "-",
          row.recordedByUserName ?? "-",
          row.recordedAt ? formatDate(row.recordedAt) : "-",
          row.sourceReferenceNumber
            ? `${row.sourceReferenceType ?? "Ref"}: ${row.sourceReferenceNumber}`
            : "-",
          `${row.inputName} (${row.inputCode})`,
          row.inputTypeName,
          formatNumber(row.quantity),
          formatNumber(row.signedQuantity),
          formatCurrency(row.unitCost),
          formatCurrency(row.signedCost),
          row.requisitionNumber ?? "-",
          row.variety ?? "-",
          row.referenceNumber ?? "-",
        ])}
      />
    </div>
  );

  const renderProfitability = (data: ProfitabilitySummaryReportResponse) => (
    <div className="space-y-4">
      <Alert>
        <AlertTitle>Profit based on recorded production costs</AlertTitle>
        <AlertDescription>
          {data.totalRevenue > 0 && data.totalCosts === 0
            ? "Recorded costs total zero. Confirm that production costs are recorded and linked before relying on the calculated 100% margin. "
            : "Costs include recorded production activities and net inventory issues linked to sowing instructions. "}
          Activity material costs and stock issue costs may describe the same consumption, so this is an operational estimate and must not be treated as an audited margin until reconciled. Sales are split equally between an order’s sowing instructions. Lifetime costs are allocated across the selected invoices; overheads are excluded.
          Sales without sowing instructions appear in their own row with no linked costs.
        </AlertDescription>
      </Alert>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Revenue" value={formatCurrency(data.totalRevenue)} />
        <MetricCard label="Total Costs" value={formatCurrency(data.totalCosts)} />
        <MetricCard label="Estimated Gross Profit" value={formatCurrency(data.grossProfit)} />
        <MetricCard label="Estimated Margin" value={formatPercent(data.profitMarginPercent)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Production Costs" value={formatCurrency(data.totalProductionCosts)} />
        <MetricCard label="Input Costs" value={formatCurrency(data.totalInputCosts)} />
        <MetricCard label="Orders" value={formatNumber(data.orderCount)} />
      </div>

      <ReportTable
        title="Monthly Breakdown"
        numericColumns={[1, 2, 3, 4, 5]}
        columns={["Month", "Revenue", "Recorded Costs", "Estimated Profit", "Margin", "Orders"]}
        rows={data.monthlyBreakdown.map((item) => [
          `${item.monthName} ${item.year}`,
          formatCurrency(item.revenue),
          formatCurrency(item.costs),
          formatCurrency(item.profit),
          formatPercent(item.profitMarginPercent),
          formatNumber(item.orderCount),
        ])}
      />

      <ReportTable
        title="Profit By Crop Type"
        numericColumns={[1, 2, 3, 4, 5]}
        columns={["Crop Type", "Allocated Revenue", "Recorded Costs", "Estimated Profit", "Margin", "Orders"]}
        rows={data.profitByCropType.map((item) => [
          item.cropTypeName,
          formatCurrency(item.revenue),
          formatCurrency(item.costs),
          formatCurrency(item.profit),
          formatPercent(item.profitMarginPercent),
          formatNumber(item.orderCount),
        ])}
      />
    </div>
  );

  const renderCostComparison = (data: CostComparisonReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Period 1 Production" value={formatCurrency(data.period1ProductionCosts)} />
        <MetricCard label="Period 2 Production" value={formatCurrency(data.period2ProductionCosts)} />
        <MetricCard label="Production Change" value={formatCurrency(data.productionCostChange)} />
        <MetricCard
          label="Production Change %"
          value={formatPercent(data.productionCostChangePercent)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Period 1 Input" value={formatCurrency(data.period1InputCosts)} />
        <MetricCard label="Period 2 Input" value={formatCurrency(data.period2InputCosts)} />
        <MetricCard label="Input Change" value={formatCurrency(data.inputCostChange)} />
        <MetricCard label="Input Change %" value={formatPercent(data.inputCostChangePercent)} />
      </div>

      <ReportTable
        title="Activity Type Cost Changes"
        columns={[
          "Activity Type",
          "Period 1",
          "Period 2",
          "Change",
          "Change %",
        ]}
        rows={data.activityTypeChanges.map((item) => [
          item.activityTypeName,
          formatCurrency(item.period1Cost),
          formatCurrency(item.period2Cost),
          formatCurrency(item.change),
          formatPercent(item.changePercent),
        ])}
      />
    </div>
  );

  const renderOrderProfitability = (data: OrderProfitabilityReportResponse) => {
    const scalarEntries = Object.entries(data).filter(([, value]) => {
      const valueType = typeof value;
      return valueType === "string" || valueType === "number" || valueType === "boolean";
    });

    const arrayEntries = Object.entries(data).filter(([, value]) => Array.isArray(value));

    return (
      <div className="space-y-4">
        {scalarEntries.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {scalarEntries.map(([key, value]) => (
              <MetricCard
                key={key}
                label={key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
                value={typeof value === "number" ? formatNumber(value) : String(value)}
              />
            ))}
          </div>
        )}

        {arrayEntries.map(([key, value]) => {
          const rows = Array.isArray(value) ? value : [];
          const firstObjectRow = rows.find(
            (row) => row !== null && typeof row === "object" && !Array.isArray(row)
          ) as Record<string, unknown> | undefined;

          const columns = firstObjectRow ? Object.keys(firstObjectRow) : ["value"];

          return (
            <ReportTable
              key={key}
              title={key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
              columns={columns}
              rows={rows.map((row) => {
                if (row && typeof row === "object" && !Array.isArray(row)) {
                  const objectRow = row as Record<string, unknown>;
                  return columns.map((column) => String(objectRow[column] ?? "-"));
                }

                return [String(row)];
              })}
            />
          );
        })}

        {scalarEntries.length === 0 && arrayEntries.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              No summary fields to display for this report.
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderPaymentSummary = (data: PaymentSummaryReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Total Received" value={formatCurrency(data.totalReceived)} />
        <MetricCard label="Total Refunded" value={formatCurrency(data.totalRefunded)} />
        <MetricCard label="Net Amount" value={formatCurrency(data.netAmount)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Payment Count" value={formatNumber(data.paymentCount)} />
        <MetricCard label="Refund Count" value={formatNumber(data.refundCount)} />
        <MetricCard
          label="Unreconciled Count"
          value={formatNumber(data.unreconciledCount)}
        />
      </div>
    </div>
  );

  const renderRevenueSummary = (data: RevenueSummaryReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Invoiced" value={formatCurrency(data.totalInvoiced)} />
        <MetricCard label="Total Paid" value={formatCurrency(data.totalPaid)} />
        <MetricCard
          label="Total Outstanding"
          value={formatCurrency(data.totalOutstanding)}
        />
        <MetricCard label="Total Overdue" value={formatCurrency(data.totalOverdue)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total Voided" value={formatCurrency(data.totalVoided)} />
        <MetricCard label="VAT Collected" value={formatCurrency(data.vatCollected)} />
        <MetricCard label="Net Revenue" value={formatCurrency(data.netRevenue)} />
        <MetricCard label="Invoice Count" value={formatNumber(data.invoiceCount)} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard label="Paid Count" value={formatNumber(data.paidCount)} />
        <MetricCard label="Overdue Count" value={formatNumber(data.overdueCount)} />
        <MetricCard label="Voided Count" value={formatNumber(data.voidedCount)} />
      </div>
    </div>
  );

  const renderRevenueByPeriod = (data: RevenueByPeriodReportResponse) => {
    const periods = Array.isArray(data.periods) ? data.periods : [];
    const firstRow = periods.find(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    ) as Record<string, unknown> | undefined;

    const columns = firstRow ? Object.keys(firstRow) : ["value"];

    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard label="From" value={data.from ? formatDate(data.from) : "-"} />
          <MetricCard label="To" value={data.to ? formatDate(data.to) : "-"} />
          <MetricCard
            label="Granularity"
            value={String(data.granularity ?? submittedFilters.granularity ?? "month")}
          />
          <MetricCard label="Periods" value={formatNumber(periods.length)} />
        </div>

        <ReportTable
          title="Revenue By Period"
          columns={columns}
          rows={periods.map((row) => {
            if (row && typeof row === "object" && !Array.isArray(row)) {
              const objectRow = row as Record<string, unknown>;
              return columns.map((column) => String(objectRow[column] ?? "-"));
            }

            return [String(row)];
          })}
        />
      </div>
    );
  };

  const renderRevenueByCustomer = (data: RevenueByCustomerReportResponse) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="From" value={formatDate(data.from)} />
        <MetricCard label="To" value={formatDate(data.to)} />
        <MetricCard label="Grand Total" value={formatCurrency(data.grandTotal)} />
        <MetricCard label="Customers" value={formatNumber(data.customers.length)} />
      </div>

      <ReportTable
        title="Revenue By Customer"
        columns={[
          "Customer",
          "Total Invoiced",
          "Total Paid",
          "Outstanding",
          "Invoice Count",
          "Last Invoice",
          "Last Payment",
        ]}
        rows={data.customers.map((item) => [
          item.customerName,
          formatCurrency(item.totalInvoiced),
          formatCurrency(item.totalPaid),
          formatCurrency(item.outstanding),
          formatNumber(item.invoiceCount),
          item.lastInvoiceDate ? formatDate(item.lastInvoiceDate) : "-",
          item.lastPaymentDate ? formatDate(item.lastPaymentDate) : "-",
        ])}
      />
    </div>
  );

  const renderRevenueByProduct = (data: RevenueByProductReportResponse) => {
    const products = Array.isArray(data.products) ? data.products : [];
    const firstRow = products.find(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    ) as Record<string, unknown> | undefined;

    const columns = firstRow ? Object.keys(firstRow) : ["value"];

    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="From"
            value={data.from ? formatDate(data.from) : submittedFilters.startDate ? formatDate(submittedFilters.startDate) : "-"}
          />
          <MetricCard
            label="To"
            value={data.to ? formatDate(data.to) : submittedFilters.endDate ? formatDate(submittedFilters.endDate) : "-"}
          />
          <MetricCard
            label="Grand Total"
            value={formatCurrency(Number(data.grandTotal ?? 0))}
          />
          <MetricCard label="Products" value={formatNumber(products.length)} />
        </div>

        <ReportTable
          title="Revenue By Product"
          columns={columns}
          rows={products.map((row) => {
            if (row && typeof row === "object" && !Array.isArray(row)) {
              const objectRow = row as Record<string, unknown>;
              return columns.map((column) => String(objectRow[column] ?? "-"));
            }

            return [String(row)];
          })}
        />
      </div>
    );
  };

  const renderRevenueAccountsReceivableSummary = (
    data: RevenueAccountsReceivableSummaryReportResponse
  ) => (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="As Of" value={formatDate(data.asOf)} />
        <MetricCard
          label="Total Outstanding"
          value={formatCurrency(data.totalOutstanding)}
        />
        <MetricCard label="Total Overdue" value={formatCurrency(data.totalOverdue)} />
        <MetricCard
          label="Outstanding Invoices"
          value={formatNumber(data.outstandingInvoiceCount)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          label="Overdue Invoices"
          value={formatNumber(data.overdueInvoiceCount)}
        />
        <MetricCard
          label="Aging Buckets"
          value={formatNumber(data.agingBuckets.length)}
        />
        <MetricCard label="Top Debtors" value={formatNumber(data.topDebtors.length)} />
      </div>

      <ReportTable
        title="Aging Buckets"
        columns={["Label", "Days From", "Days To", "Amount", "Invoice Count"]}
        rows={data.agingBuckets.map((bucket) => [
          bucket.label,
          formatNumber(bucket.daysFrom),
          formatNumber(bucket.daysTo),
          formatCurrency(bucket.amount),
          formatNumber(bucket.invoiceCount),
        ])}
      />

      <ReportTable
        title="Top Debtors"
        columns={[
          "Customer",
          "Outstanding",
          "Overdue",
          "Overdue Invoices",
          "Oldest Due Date",
        ]}
        rows={data.topDebtors.map((debtor) => [
          debtor.customerName,
          formatCurrency(debtor.outstanding),
          formatCurrency(debtor.overdue),
          formatNumber(debtor.overdueInvoiceCount),
          debtor.oldestDueDate ? formatDate(debtor.oldestDueDate) : "-",
        ])}
      />
    </div>
  );

  const renderReportContent = () => {
    if (reportQuery.isFetching && !reportData) {
      return <div role="status" aria-label="Loading report" className="space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>;
    }
    if (reportQuery.isError && !reportData) return null;
    if (!reportData) {
      return (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Choose filters and click <span className="font-medium">Run Report</span>.
          </CardContent>
        </Card>
      );
    }

    switch (selectedReport) {
      case "production-costs":
        return renderProductionCosts(reportData as ProductionCostsReportResponse);
      case "input-costs":
        return renderInputCosts(reportData as InputCostsReportResponse);
      case "material-usage":
        return renderMaterialUsage(reportData as MaterialUsageReportResponse);
      case "profitability-summary":
        return renderProfitability(reportData as ProfitabilitySummaryReportResponse);
      case "cost-comparison":
        return renderCostComparison(reportData as CostComparisonReportResponse);
      case "order-profitability":
        return renderOrderProfitability(reportData as OrderProfitabilityReportResponse);
      case "payment-summary":
        return renderPaymentSummary(reportData as PaymentSummaryReportResponse);
      case "revenue-summary":
        return renderRevenueSummary(reportData as RevenueSummaryReportResponse);
      case "revenue-by-period":
        return renderRevenueByPeriod(reportData as RevenueByPeriodReportResponse);
      case "revenue-by-customer":
        return renderRevenueByCustomer(reportData as RevenueByCustomerReportResponse);
      case "revenue-by-product":
        return renderRevenueByProduct(reportData as RevenueByProductReportResponse);
      case "revenue-ar-summary":
        return renderRevenueAccountsReceivableSummary(
          reportData as RevenueAccountsReceivableSummaryReportResponse
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground mt-1">Run financial and operational reports. Calendar dates use Kenya time (EAT), including the full end date.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Field>
              <FieldLabel>Report Type</FieldLabel>
              <Select
                value={isRevenueReport(selectedReport) ? "revenue-summary" : selectedReport}
                onValueChange={handleReportTypeChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {isRevenueReport(selectedReport) && (
              <Field>
                <FieldLabel>Revenue Report Filter</FieldLabel>
                <Select value={selectedReport} onValueChange={handleRevenueReportTypeChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select revenue report" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVENUE_REPORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {selectedReport !== "cost-comparison" && selectedReport !== "order-profitability" && (
              <>
                {selectedReport !== "revenue-ar-summary" && (
                  <Field className="lg:col-span-2">
                    <FieldLabel>{selectedReport === "profitability-summary" ? "Invoice Date Range" : "Date Range"}</FieldLabel>
                    <DateRangePicker
                      startDate={filters.startDate ?? ""}
                      endDate={filters.endDate ?? ""}
                      onChange={({ startDate, endDate }) => {
                        updateFilter("startDate", startDate ?? "")
                        updateFilter("endDate", endDate ?? "")
                      }}
                    />
                  </Field>
                )}
              </>
            )}

            {selectedReport === "revenue-ar-summary" && (
              <Field>
                <FieldLabel>As Of</FieldLabel>
                  <DatePicker
                  value={filters.asOf ?? ""}
                    onChange={(value) => updateFilter("asOf", value)}
                />
              </Field>
            )}

            {(selectedReport === "payment-summary" || selectedReport === "revenue-summary") && (
              <Field>
                <FieldLabel>Order (Optional)</FieldLabel>
                <ReportLookup
                  kind="order"
                  value={filters.orderId}
                  onChange={(value) => updateFilter("orderId", value)}
                  placeholder="All Orders"
                />
              </Field>
            )}

            {selectedReport === "revenue-by-period" && (
              <Field>
                <FieldLabel>Granularity</FieldLabel>
                <Select
                  value={filters.granularity ?? "month"}
                  onValueChange={(value) => updateFilter("granularity", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select granularity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>

          {selectedReport === "order-profitability" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field>
                <FieldLabel>Order</FieldLabel>
                <ReportLookup
                  kind="order"
                  value={filters.orderId}
                  onChange={(value) => updateFilter("orderId", value)}
                  placeholder="Select Order"
                />
              </Field>
            </div>
          )}

          {selectedReport === "cost-comparison" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field className="md:col-span-2">
                <FieldLabel>Period 1</FieldLabel>
                <DateRangePicker
                  startDate={filters.period1Start ?? ""}
                  endDate={filters.period1End ?? ""}
                  onChange={({ startDate, endDate }) => {
                    updateFilter("period1Start", startDate ?? "")
                    updateFilter("period1End", endDate ?? "")
                  }}
                />
              </Field>

              <Field className="md:col-span-2">
                <FieldLabel>Period 2</FieldLabel>
                <DateRangePicker
                  startDate={filters.period2Start ?? ""}
                  endDate={filters.period2End ?? ""}
                  onChange={({ startDate, endDate }) => {
                    updateFilter("period2Start", startDate ?? "")
                    updateFilter("period2End", endDate ?? "")
                  }}
                />
              </Field>
            </div>
          )}

          {selectedReport === "production-costs" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field>
                <FieldLabel>Greenhouse</FieldLabel>
                <Select
                  value={filters.greenhouseId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("greenhouseId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Greenhouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Greenhouses</SelectItem>
                    {greenhouses.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.greenhouseName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Field ID</FieldLabel>
                <div className="flex gap-2">
                  <Input value={filters.fieldId ?? ""} onChange={e => updateFilter("fieldId", e.target.value)} placeholder="Field ID" />
                  {filters.fieldId && <Button type="button" variant="outline" onClick={() => updateFilter("fieldId", "")}>Clear</Button>}
                </div>
              </Field>

              <Field>
                <FieldLabel>Sowing Instruction</FieldLabel>
                <ReportLookup
                  kind="instruction"
                  value={filters.sowingInstructionId}
                  onChange={(value) => updateFilter("sowingInstructionId", value)}
                  placeholder="All Instructions"
                />
              </Field>

              <Field>
                <FieldLabel>Requisition ID</FieldLabel>
                <div className="flex gap-2">
                  <Input value={filters.requisitionId ?? ""} onChange={e => updateFilter("requisitionId", e.target.value)} placeholder="Requisition ID" />
                  {filters.requisitionId && <Button type="button" variant="outline" onClick={() => updateFilter("requisitionId", "")}>Clear</Button>}
                </div>
              </Field>
            </div>
          )}

          {selectedReport === "input-costs" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field>
                <FieldLabel>Input Type</FieldLabel>
                <Select
                  value={filters.inputTypeId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("inputTypeId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Input Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Input Types</SelectItem>
                    {inputTypes.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Supplier</FieldLabel>
                <Select
                  value={filters.supplierId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("supplierId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Suppliers</SelectItem>
                    {suppliers.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.supplierName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {selectedReport === "material-usage" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field>
                <FieldLabel>Department</FieldLabel>
                <Select
                  value={filters.departmentId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("departmentId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Departments</SelectItem>
                    {departments.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Greenhouse</FieldLabel>
                <Select
                  value={filters.greenhouseId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("greenhouseId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Greenhouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Greenhouses</SelectItem>
                    {greenhouses.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.greenhouseName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Input Type</FieldLabel>
                <Select
                  value={filters.inputTypeId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("inputTypeId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Input Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Input Types</SelectItem>
                    {inputTypes.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Input</FieldLabel>
                <div className="flex gap-2">
                  <InputLookup value={filters.inputId ?? ""} onSelect={(input) => updateFilter("inputId", input.id)} placeholder="All Inputs" />
                  {filters.inputId && <Button type="button" variant="outline" onClick={() => updateFilter("inputId", "")}>Clear</Button>}
                </div>
              </Field>

              <Field>
                <FieldLabel>Crop Type</FieldLabel>
                <Select
                  value={filters.cropTypeId ?? "__all"}
                  onValueChange={(value) =>
                    updateFilter("cropTypeId", value === "__all" ? "" : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Crop Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Crop Types</SelectItem>
                    {cropTypes.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Variety</FieldLabel>
                <div className="flex gap-2">
                  <Input
                    value={filters.variety ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateFilter("variety", e.target.value)
                    }
                    placeholder="e.g. Ansal F1"
                  />
                  {filters.variety && <Button type="button" variant="outline" onClick={() => updateFilter("variety", "")}>Clear</Button>}
                </div>
              </Field>

              <Field>
                <FieldLabel>Sowing Instruction</FieldLabel>
                <ReportLookup
                  kind="instruction"
                  value={filters.sowingInstructionId}
                  onChange={(value) => updateFilter("sowingInstructionId", value)}
                  placeholder="All Instructions"
                />
              </Field>

              <Field>
                <FieldLabel>Group By</FieldLabel>
                <Select
                  value={filters.groupBy ?? "input"}
                  onValueChange={(value) => updateFilter("groupBy", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Grouping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="input">Input</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                    <SelectItem value="inputType">Input Type</SelectItem>
                    <SelectItem value="greenhouse">Greenhouse</SelectItem>
                    <SelectItem value="field">Field</SelectItem>
                    <SelectItem value="variety">Variety</SelectItem>
                    <SelectItem value="requisition">Requisition</SelectItem>
                    <SelectItem value="day">Day</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>Source Type</FieldLabel>
                <Input
                  value={filters.sourceReferenceType ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilter("sourceReferenceType", e.target.value)
                  }
                  placeholder="e.g. Requisition"
                />
              </Field>

              <Field>
                <FieldLabel>Source Number</FieldLabel>
                <Input
                  value={filters.sourceReferenceNumber ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateFilter("sourceReferenceNumber", e.target.value)
                  }
                  placeholder="e.g. REQ-00123"
                />
              </Field>
            </div>
          )}

          {selectedReport === "profitability-summary" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Field>
                <FieldLabel>Customer</FieldLabel>
                <CustomerPicker
                  value={filters.customerId ?? ""}
                  onChange={(v) => updateFilter("customerId", v)}
                  title="All Customers"
                  allowClear
                />
              </Field>

              <Field>
                <FieldLabel>Crop Type</FieldLabel>
                <Select
                  value={filters.cropTypeId ?? "__all"}
                  onValueChange={(v) => updateFilter("cropTypeId", v === "__all" ? "" : v)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Crop Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Crop Types</SelectItem>
                    {cropTypes.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {validationError && (
            <p className="text-sm text-red-600">{validationError}</p>
          )}

          {reportData && hasUnappliedFilters && <p role="status" className="text-sm text-muted-foreground">Filters have changed. Run the report to update these results. Exports use the last run’s filters.</p>}

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={handleRunReport} disabled={reportQuery.isFetching}>
              {reportQuery.isFetching ? "Running..." : "Run Report"}
            </Button>

            {(selectedReport === "production-costs" ||
              selectedReport === "input-costs" ||
              selectedReport === "cost-comparison" ||
              selectedReport === "profitability-summary" ||
              selectedReport === "order-profitability" ||
              selectedReport === "revenue-summary" ||
              selectedReport === "revenue-by-period" ||
              selectedReport === "revenue-by-customer" ||
              selectedReport === "revenue-by-product" ||
              selectedReport === "revenue-ar-summary") && (
              <Button
                variant="secondary"
                onClick={handleExportExcel}
                disabled={!canExportExcel || isExportingExcel || reportQuery.isFetching}
              >
                {isExportingExcel ? "Exporting..." : "Export Excel"}
              </Button>
            )}

            {(selectedReport === "production-costs" ||
              selectedReport === "input-costs" ||
              selectedReport === "profitability-summary" ||
              selectedReport === "cost-comparison" ||
              selectedReport === "order-profitability" ||
              selectedReport === "revenue-summary" ||
              selectedReport === "revenue-by-period" ||
              selectedReport === "revenue-by-customer" ||
              selectedReport === "revenue-by-product" ||
              selectedReport === "revenue-ar-summary") && (
              <Button
                variant="secondary"
                onClick={handleExportPdf}
                disabled={!canExportPdf || isExportingPdf || reportQuery.isFetching}
              >
                {isExportingPdf ? "Exporting..." : "Export PDF"}
              </Button>
            )}

            <Button variant="outline" onClick={handleResetFilters}>
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {runToken > 0 && reportQuery.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          Failed to run report. Please check filters or try again.
        </div>
      )}

      {renderReportContent()}
    </div>
  );
};
